import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack, Check } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { UserStatus } from '../types';
import { apiClient } from '../utils/api';

const KakaoRegistrationPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: boolean}>({});
  
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [kakaoUserData, setKakaoUserData] = useState<any>(null);
  const [formData, setFormData] = useState({
    birthDate: '',
    gender: '' as 'male' | 'female' | '',
    job: '',
    location: '',
    interests: [] as string[],
    selfIntroduction: '',
    preferredAge: '' as 'older' | 'younger' | 'any' | '',
    profileImageUrl: '' as string,
  });

  const steps = ['카카오 간편 인증', '프로필 정보', '매칭 선호도'];
  
  const availableInterests = [
    '요리', '등산', '독서', '영화감상', '음악감상', '여행', 
    '운동', '원예', '사진', '그림그리기', '낚시', '골프',
    '댄스', '봉사활동', '문화생활', '컴퓨터', '새로운 언어 배우기', '종교활동'
  ];

  const locations = [
    '서울시', '부산시', '대구시', '인천시', '광주시', '대전시', '울산시', '세종시',
    '경기도', '강원도', '충청북도', '충청남도', '전라북도', '전라남도', '경상북도', '경상남도', '제주도'
  ];

  useEffect(() => {
    // 세션 스토리지에서 카카오 사용자 정보 가져오기
    const storedKakaoData = sessionStorage.getItem('kakaoUserData');
    if (storedKakaoData) {
      const userData = JSON.parse(storedKakaoData);
      setKakaoUserData(userData);
      
      // 카카오 정보에서 성별과 생년월일 추출 (가능한 경우)
      if (userData.gender) {
        setFormData(prev => ({ ...prev, gender: userData.gender }));
      }
      if (userData.birthDate) {
        setFormData(prev => ({ ...prev, birthDate: userData.birthDate }));
      }
    } else {
      // 카카오 정보가 없으면 로그인 페이지로 리다이렉트
      navigate('/login');
    }

    // 이미 인증이 완료된 사용자인지 확인
    if (user && user.status === UserStatus.APPROVED) {
      navigate('/dashboard');
    }

    // 새로운 통합 플로우로 리다이렉트 (기존 카카오 등록 페이지는 더 이상 사용하지 않음)
    if (storedKakaoData) {
      const userData = JSON.parse(storedKakaoData);
      sessionStorage.removeItem('kakaoUserData');
      sessionStorage.setItem('registrationUserData', JSON.stringify({
        type: 'kakao',
        ...userData
      }));
      navigate('/register/profile', { replace: true });
      return;
    }

    // 뒤로가기 방지 및 처리
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      navigate('/register', { replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    
    // 현재 페이지를 히스토리에 추가
    window.history.pushState(null, '', window.location.pathname);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [user, navigate]);

  const handleNext = () => {
    if (activeStep === 0 && !validateKakaoAuth()) return;
    if (activeStep === 1 && !validateProfile()) return;
    
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep(activeStep + 1);
    }
    setError('');
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    setError('');
  };

  const validateKakaoAuth = () => {
    // 카카오 사용자 데이터가 있는지 확인
    if (!kakaoUserData) {
      setError('카카오 로그인 정보를 찾을 수 없습니다. 다시 로그인해주세요.');
      return false;
    }
    return true;
  };

  const validateProfile = () => {
    const errors: {[key: string]: boolean} = {};
    let hasError = false;

    // 각 필드 검증
    if (!formData.birthDate) {
      errors.birthDate = true;
      hasError = true;
    }
    if (!formData.gender) {
      errors.gender = true;
      hasError = true;
    }
    if (!formData.job) {
      errors.job = true;
      hasError = true;
    }
    if (!formData.location) {
      errors.location = true;
      hasError = true;
    }
    if (formData.interests.length === 0) {
      errors.interests = true;
      hasError = true;
    }
    if (!formData.selfIntroduction.trim()) {
      errors.selfIntroduction = true;
      hasError = true;
    } else if (formData.selfIntroduction.length < 10) {
      errors.selfIntroduction = true;
      hasError = true;
      setError('자기소개는 최소 10자 이상 입력해주세요.');
      setFieldErrors(errors);
      return false;
    }

    if (formData.birthDate) {
      const birthYear = new Date(formData.birthDate).getFullYear();
      const currentYear = new Date().getFullYear();
      const age = currentYear - birthYear;
      
      if (age < 50 || age > 70) {
        errors.birthDate = true;
        hasError = true;
        setError('50세에서 70세 사이의 분만 가입하실 수 있습니다.');
        setFieldErrors(errors);
        return false;
      }
    }

    if (hasError) {
      setError('모든 필드를 입력해주세요. 관심사는 최소 1개 이상 선택해주세요.');
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    return true;
  };


  const handleSubmit = async () => {
    if (!formData.preferredAge) {
      setError('연령대 선호도를 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      const registrationData = {
        // 카카오 정보
        kakaoId: kakaoUserData.kakaoId,
        email: kakaoUserData.email,
        name: kakaoUserData.name,
        profileImage: kakaoUserData.profileImage,
        
        // 사용자 입력 정보
        birthDate: new Date(formData.birthDate),
        gender: formData.gender as 'male' | 'female',
        job: formData.job,
        location: formData.location,
        interests: formData.interests,
        selfIntroduction: formData.selfIntroduction,
        preferredAge: formData.preferredAge as 'older' | 'younger' | 'any',
        profileImageUrl: formData.profileImageUrl || undefined, // 프로필 이미지 URL
      };
      
      const updatedUser = await apiClient.completeKakaoRegistration(registrationData);
      
      // 세션 스토리지 정리
      sessionStorage.removeItem('kakaoUserData');
      
      if (updateUser) {
        updateUser(updatedUser);
      }
      setShowSuccess(true);
    } catch (err: any) {
      setError(err.message || '가입 신청에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleInterestToggle = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const renderKakaoAuthStep = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="h5" gutterBottom>
        카카오 정보 확인
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        카카오 계정에서 가져온 정보를 확인해주세요.
      </Typography>
      
      {kakaoUserData ? (
        <Box>
          <Check color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6" color="success.main" gutterBottom>
            카카오 정보 확인 완료
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {kakaoUserData.name || '사용자'}님, 환영합니다!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            이메일: {kakaoUserData.email || '이메일 정보 없음'}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            카카오 정보를 찾을 수 없습니다.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{
              backgroundColor: '#FEE500',
              color: '#000',
              py: 2,
              px: 4,
              '&:hover': {
                backgroundColor: '#FDD835',
              },
            }}
          >
            다시 로그인하기
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderProfileStep = () => (
    <Box>
      <TextField
        margin="normal"
        required
        fullWidth
        id="birthDate"
        label="생년월일"
        type="date"
        InputLabelProps={{ shrink: true }}
        value={formData.birthDate}
        onChange={(e) => setFormData(prev => ({ ...prev, birthDate: e.target.value }))}
        helperText="50세~70세만 가입 가능합니다"
        error={fieldErrors.birthDate}
      />
      
      <FormControl component="fieldset" margin="normal" required error={fieldErrors.gender}>
        <FormLabel component="legend">성별</FormLabel>
        <RadioGroup
          row
          value={formData.gender}
          onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value as 'male' | 'female' }))}
        >
          <FormControlLabel value="male" control={<Radio />} label="남성" />
          <FormControlLabel value="female" control={<Radio />} label="여성" />
        </RadioGroup>
      </FormControl>

      <TextField
        margin="normal"
        required
        fullWidth
        id="job"
        label="직업"
        name="job"
        value={formData.job}
        onChange={(e) => setFormData(prev => ({ ...prev, job: e.target.value }))}
        placeholder="예: 퇴직, 자영업, 회사원 등"
        error={fieldErrors.job}
      />
      
      <FormControl fullWidth margin="normal" required error={fieldErrors.location}>
        <InputLabel>거주 지역</InputLabel>
        <Select
          value={formData.location}
          label="거주 지역"
          onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
        >
          {locations.map((location) => (
            <MenuItem key={location} value={location}>
              {location}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <Typography variant="h6" sx={{ mt: 3, mb: 2, color: fieldErrors.interests ? 'error.main' : 'inherit' }}>
        관심사 (여러 개 선택 가능) *
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 1,
        border: fieldErrors.interests ? '2px solid #d32f2f' : 'none',
        borderRadius: fieldErrors.interests ? 1 : 0,
        padding: fieldErrors.interests ? 1 : 0
      }}>
        {availableInterests.map((interest) => (
          <Chip
            key={interest}
            label={interest}
            clickable
            color={formData.interests.includes(interest) ? 'primary' : 'default'}
            onClick={() => handleInterestToggle(interest)}
          />
        ))}
      </Box>
      <Typography variant="body2" color={fieldErrors.interests ? 'error.main' : 'text.secondary'} sx={{ mt: 1, mb: 2 }}>
        선택된 관심사: {formData.interests.length}개 {fieldErrors.interests && '(최소 1개 이상 선택해주세요)'}
      </Typography>

      <TextField
        margin="normal"
        required
        fullWidth
        multiline
        rows={4}
        id="selfIntroduction"
        label="자기소개"
        name="selfIntroduction"
        value={formData.selfIntroduction}
        onChange={(e) => setFormData(prev => ({ ...prev, selfIntroduction: e.target.value }))}
        placeholder="자신을 소개해주세요. (최소 10자 이상)"
        helperText={`${formData.selfIntroduction.length}자 (최소 10자 필요)`}
        error={fieldErrors.selfIntroduction}
      />
    </Box>
  );

  const renderPreferenceStep = () => (
    <Box>
      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend" sx={{ mb: 2 }}>
          연령대 선호도
        </FormLabel>
        <RadioGroup
          value={formData.preferredAge}
          onChange={(e) => setFormData(prev => ({ ...prev, preferredAge: e.target.value as 'older' | 'younger' | 'any' }))}
        >
          <FormControlLabel value="older" control={<Radio />} label="연상 (나보다 나이가 많은 분)" />
          <FormControlLabel value="younger" control={<Radio />} label="연하 (나보다 나이가 적은 분)" />
          <FormControlLabel value="any" control={<Radio />} label="상관없음" />
        </RadioGroup>
      </FormControl>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          • 가입신청 후 관리자 승인을 거쳐 정식 회원이 됩니다.<br />
          • 가입 승인 시 10개의 하트가 지급됩니다.<br />
          • 매주 새로운 큐피트 카드를 받아보실 수 있습니다.
        </Typography>
      </Alert>
    </Box>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderKakaoAuthStep();
      case 1:
        return renderProfileStep();
      case 2:
        return renderPreferenceStep();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/register')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div">
            가입 신청
          </Typography>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              Silver Connect
            </Typography>
            <Typography variant="h6" color="text.secondary">
              새로운 인연의 시작
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ px: 3 }}
            >
              이전
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading || (activeStep === 0 && !kakaoUserData)}
              sx={{ px: 3 }}
            >
              {loading ? '처리 중...' : activeStep === steps.length - 1 ? '가입 신청 완료' : '다음'}
            </Button>
          </Box>
        </Paper>
      </Container>

      <Dialog open={showSuccess} onClose={() => setShowSuccess(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Check color="success" sx={{ fontSize: 64 }} />
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
            가입신청이 완료되었습니다!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            관리자 승인 후 서비스를 이용하실 수 있습니다.<br />
            승인 완료 시 이메일로 안내해드립니다.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowSuccess(false);
              navigate('/dashboard');
            }}
          >
            대시보드로 이동
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KakaoRegistrationPage;